import '@/styles/calendar-layout.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IngameFlash, useIngameFlash } from '@/components/IngameFlash';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { assetUrl, itemIconFallbackUrl, resolveMediaUrl } from '@/lib/assetUrl';
import {
  useCalendar,
  useCalendarMutations,
  type CalendarBonusReward,
  type CalendarDayCell,
} from '@/hooks/useCalendar';

function formatDate(value: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

function formatDaysRemaining(
  remaining: number | string,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const days = typeof remaining === 'number' ? remaining : Number(remaining);
  if (!Number.isFinite(days) || days < 0) return '';
  if (days === 1) return t('cal_day_remaining');
  return t('cal_days_remaining', { days });
}

function bonusMilestoneLabel(
  day: number,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  if (day >= 28) return t('cal_bonus_1_month');
  if (day === 1) return t('cal_bonus_n_day');
  return t('cal_bonus_n_days', { days: day });
}

/** Hitos = filas is_consecutive=1, day_number menor → mayor. */
function buildBonusMilestones(data: {
  bonusRewards?: CalendarBonusReward[];
  bonusReward: CalendarBonusReward | null;
} | undefined): CalendarBonusReward[] {
  if (!data) return [];
  const consecutive = [...(data.bonusRewards ?? [])].sort((a, b) => a.day - b.day);
  if (consecutive.length > 0) return consecutive;
  return data.bonusReward ? [data.bonusReward] : [];
}

function CalendarDayCard({
  day,
  reward,
  isToday,
  canCheckinToday,
  isMilestone,
  eager,
  onClaim,
}: {
  day: number;
  reward: CalendarDayCell;
  isToday: boolean;
  canCheckinToday: boolean;
  isMilestone?: boolean;
  eager?: boolean;
  onClaim: (day: number, payload: { CodeName128: string; item_quantity: number }) => Promise<boolean>;
}) {
  const { t } = useLocale();
  const [claimPhase, setClaimPhase] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [visualStatus, setVisualStatus] = useState(reward.status);
  const popTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setVisualStatus(reward.status);
    setClaimPhase('idle');
  }, [reward.status, reward.day]);

  useEffect(
    () => () => {
      if (popTimer.current) clearTimeout(popTimer.current);
    },
    [],
  );

  const isClaimed = visualStatus === 2 || claimPhase === 'success';
  const canClaim =
    !isClaimed &&
    claimPhase === 'idle' &&
    (visualStatus === 1 || (isToday && visualStatus === 0 && canCheckinToday));
  const isLocked = !isClaimed && !canClaim;
  const isRare = reward.CodeName128.includes('_RARE');

  const handleClaim = async () => {
    if (!canClaim) return;
    setClaimPhase('pending');
    await new Promise((r) => setTimeout(r, 600));
    const ok = await onClaim(day, {
      CodeName128: reward.CodeName128,
      item_quantity: reward.item_quantity,
    });
    if (ok) {
      setVisualStatus(2);
      setClaimPhase('success');
      popTimer.current = setTimeout(() => setClaimPhase('idle'), 700);
    } else {
      setClaimPhase('error');
      popTimer.current = setTimeout(() => setClaimPhase('idle'), 600);
    }
  };

  const imgClass = [
    'cal-day-img',
    claimPhase === 'pending' ? 'reward-claim-absorb' : '',
    claimPhase === 'success' ? 'reward-claim-pop' : '',
    claimPhase === 'error' ? 'reward-claim-error' : '',
    isClaimed ? 'is-claimed' : '',
    isLocked ? 'is-locked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const cardClass = [
    'cal-day-card',
    isMilestone ? 'cal-day-card--milestone' : '',
    isToday ? 'is-today' : '',
    isClaimed ? 'is-claimed' : '',
    isLocked ? 'is-locked' : '',
    canClaim ? 'is-claimable' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cardClass} data-cal-day={day}>
      {isToday && !isClaimed ? <div className="cal-day-today-label">{t('cal_today')}</div> : null}
      <div className="cal-day-number">{t('cal_day_label', { day })}</div>

      {isClaimed ? (
        <i className="ph-fill ph-check-circle cal-card-check-top" aria-label={t('cal_claimed')} />
      ) : (
        <span className="cal-card-check-spacer" aria-hidden />
      )}

      <div className="cal-day-reward">
        <img
          src={resolveMediaUrl(reward.image)}
          alt={reward.item_name}
          className={imgClass}
          title={reward.item_name}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = itemIconFallbackUrl();
          }}
        />
        {isRare && <div className="calendar-item-plus" />}
        <div className="calendar-quantity-badge">x{reward.amount}</div>
        {claimPhase === 'pending' && <div className="reward-claim-spinner" aria-hidden />}
      </div>

      <div className="cal-day-action">
        {canClaim || claimPhase === 'pending' ? (
          <button
            type="button"
            className="cal-claim-btn"
            disabled={claimPhase === 'pending'}
            onClick={() => void handleClaim()}
            aria-label={t('cal_claim')}
          >
            {t('cal_claim')}
          </button>
        ) : isClaimed ? (
          <div
            className={`cal-claimed-badge${claimPhase === 'success' ? ' reward-claim-check-animate' : ''}`}
          >
            {t('cal_claimed')}
          </div>
        ) : (
          <i className="ph-fill ph-lock-simple cal-status-lock" aria-label={t('cal_locked')} />
        )}
      </div>
    </div>
  );
}

export function CalendarPage() {
  const { t } = useLocale();
  const { data, isLoading, isError } = useCalendar();
  const { claimDay } = useCalendarMutations();
  const { flash, showFlash } = useIngameFlash(3000);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const centeredOnce = useRef(false);

  const sortedDays = useMemo(() => {
    if (!data?.calendarData) return [];
    return Object.keys(data.calendarData)
      .map(Number)
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
  }, [data?.calendarData]);

  const milestoneDay = data?.bonusReward?.day ?? sortedDays[sortedDays.length - 1] ?? 0;

  /* Todos los días van en el carrusel (incluido el 14 / bonus); no anclar fuera. */
  const carouselDays = sortedDays;

  const consecutiveNodes = useMemo(() => {
    if (!data?.calendarData) return [];
    return sortedDays
      .map((day) => {
        const reward = data.calendarData[day] ?? data.calendarData[String(day)];
        return reward?.is_consecutive ? { day, reward } : null;
      })
      .filter(Boolean) as Array<{ day: number; reward: CalendarDayCell }>;
  }, [data?.calendarData, sortedDays]);

  /** Próximas: consecutivos si existen; si no, siguientes días del calendario. */
  const upcomingRewards = useMemo(() => {
    if (!data?.calendarData) return [];
    const today = data.todayDayNumber || 0;
    const fromConsecutive = consecutiveNodes
      .filter(({ day, reward }) => day > today && reward.status < 2)
      .slice(0, 3);
    if (fromConsecutive.length > 0) return fromConsecutive;

    return sortedDays
      .filter((day) => day > today)
      .map((day) => {
        const reward = data.calendarData[day] ?? data.calendarData[String(day)];
        return reward ? { day, reward } : null;
      })
      .filter(Boolean)
      .slice(0, 3) as Array<{ day: number; reward: CalendarDayCell }>;
  }, [consecutiveNodes, data?.calendarData, data?.todayDayNumber, sortedDays]);

  const bonusMilestones = useMemo(
    () =>
      buildBonusMilestones(
        data
          ? {
              bonusRewards: data.bonusRewards,
              bonusReward: data.bonusReward,
            }
          : undefined,
      ),
    [data],
  );

  const updateScrollEdges = useCallback(() => {
    const el = carouselRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const scrollCarousel = (dir: -1 | 1) => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('.cal-day-card');
    const step = card?.offsetWidth ?? 110;
    el.scrollBy({ left: dir * step * 3, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return undefined;
    updateScrollEdges();
    el.addEventListener('scroll', updateScrollEdges, { passive: true });
    window.addEventListener('resize', updateScrollEdges);
    return () => {
      el.removeEventListener('scroll', updateScrollEdges);
      window.removeEventListener('resize', updateScrollEdges);
    };
  }, [updateScrollEdges, carouselDays.length]);

  useEffect(() => {
    if (!data?.todayDayNumber || centeredOnce.current) return;
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>(`[data-cal-day="${data.todayDayNumber}"]`);
    if (!card) return;
    const left = card.offsetLeft - el.clientWidth / 2 + card.offsetWidth / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
    centeredOnce.current = true;
    updateScrollEdges();
  }, [data?.todayDayNumber, carouselDays.length, updateScrollEdges]);

  const handleClaim = useCallback(
    async (
      day: number,
      payload: { CodeName128: string; item_quantity: number },
    ): Promise<boolean> => {
      if (!data?.campaignId) return false;
      try {
        const result = await claimDay.mutateAsync({
          campaign_id: data.campaignId,
          day_number: day,
          CodeName128: payload.CodeName128,
          item_quantity: payload.item_quantity,
        });
        showFlash(result.message, result.success ? 'success' : 'danger');
        return result.success;
      } catch {
        showFlash(t('cal_claim_error'), 'danger');
        return false;
      }
    },
    [claimDay, data?.campaignId, showFlash, t],
  );

  if (isError && !data) {
    return (
      <div className="container-ingame fade-in calendar-page">
        <div className="calendar-container calendar-aaa calendar-error-msg">
          <SimpleMenu title={t('daily_login')} />
          {t('cal_claim_error')}
        </div>
      </div>
    );
  }

  const loading = isLoading && !data;
  // Banner estático de temporada (no depende del BP activo)
  const bannerSrc = assetUrl('/assets/images/upload/battlepass/6920e8b11e355.png');
  const description =
    data?.description?.trim() ||
    data?.campaign?.description?.trim() ||
    t('cal_default_desc');
  // Target muestra "DAYS CHECKED IN" — usamos racha consecutiva; si es 0, días marcados.
  const streak = (data?.consecutiveDays ?? 0) > 0 ? (data?.consecutiveDays ?? 0) : (data?.daysChecked ?? 0);
  const bonusDay =
    bonusMilestones[bonusMilestones.length - 1]?.day ??
    data?.bonusReward?.day ??
    data?.totalDays ??
    0;
  const bonusProgress = bonusDay > 0 ? Math.min(100, ((data?.daysChecked ?? 0) / bonusDay) * 100) : 0;

  const litNodes = consecutiveNodes.length
    ? consecutiveNodes.map(({ day }) => streak >= day || (data?.calendarData[day]?.status ?? 0) >= 1)
    : Array.from({ length: Math.min(5, Math.max(1, data?.totalDays || 5)) }, (_, i) => streak > i);

  const nodesForRail =
    consecutiveNodes.length > 0
      ? consecutiveNodes
      : Array.from({ length: Math.min(5, data?.totalDays || 5) }, (_, i) => ({
          day: i + 1,
          reward: null as CalendarDayCell | null,
        }));

  return (
    <div className="container-ingame fade-in calendar-page">
      <IngameFlash flash={flash} />

      <div className="calendar-container calendar-aaa">
        <div className="calendar-aaa-bg" aria-hidden>
          <img
            key={bannerSrc}
            src={bannerSrc}
            alt=""
            className="calendar-aaa-bg-img"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="calendar-aaa-bg-overlay" />
        </div>

        <SimpleMenu title={t('daily_login')} />

        {/* Countdown + info: top-right absoluto (como target / BP), no dentro del título */}
        <div className="calendar-aaa-top-right">
          {data ? (
            <div className="calendar-aaa-days">
              <i className="ph-light ph-hourglass-medium" aria-hidden />
              <span>{formatDaysRemaining(data.daysRemaining, t)}</span>
            </div>
          ) : null}
          <span className="calendar-aaa-days-sep" aria-hidden />
          <button
            type="button"
            className="calendar-aaa-info"
            title={t('cal_info_title')}
            aria-label={t('cal_info_title')}
            onClick={() => setInfoOpen((v) => !v)}
          >
            <i className="ph-light ph-info" aria-hidden />
          </button>
        </div>

        {infoOpen && (
          <div className="calendar-info-popover" role="dialog" aria-label={t('cal_info_title')}>
            <button
              type="button"
              className="calendar-info-popover-close"
              aria-label={t('menu_close')}
              onClick={() => setInfoOpen(false)}
            >
              <i className="ph-light ph-x" aria-hidden />
            </button>
            <p>{description}</p>
          </div>
        )}

        <header className="calendar-aaa-hero">
          <div className="calendar-aaa-hero-left">
            <div className="calendar-event-box">
              <span className="calendar-event-box-corner calendar-event-box-corner--tl" aria-hidden />
              <span className="calendar-event-box-corner calendar-event-box-corner--tr" aria-hidden />
              <span className="calendar-event-box-corner calendar-event-box-corner--bl" aria-hidden />
              <span className="calendar-event-box-corner calendar-event-box-corner--br" aria-hidden />
              <h1 className="calendar-aaa-title">
                {data?.currentEvent || (loading ? '…' : t('cal_no_campaign'))}
              </h1>
              {data?.startDate && data?.endDate ? (
                <div className="calendar-aaa-dates">
                  {t('cal_schedule_label', {
                    start: formatDate(data.startDate),
                    end: formatDate(data.endDate),
                  })}
                </div>
              ) : null}
              <div className="calendar-event-box-rule" aria-hidden>
                <span className="calendar-event-box-diamond" />
              </div>
              <p className="calendar-aaa-desc">{description}</p>
            </div>
          </div>

          <div className="calendar-aaa-hero-center">
            <div className="calendar-streak-block">
              <div className="calendar-streak-eyebrow">{t('cal_consecutive_login')}</div>
              <div className="calendar-streak-value">{streak}</div>
              <div className="calendar-streak-label">{t('cal_streak_label')}</div>
              <div className="calendar-streak-rail" aria-hidden>
                {nodesForRail.map((node, i) => (
                  <span
                    key={`node-${node.day}`}
                    className={`calendar-streak-diamond${litNodes[i] ? ' is-lit' : ''}`}
                  />
                ))}
              </div>
            </div>

            <div className="calendar-upcoming">
              <div className="calendar-upcoming-banner">
                <span>{t('cal_upcoming_rewards')}</span>
              </div>
              <div className="calendar-upcoming-list">
                {upcomingRewards.length === 0 ? (
                  <span className="calendar-upcoming-empty">
                    {loading ? '…' : t('cal_no_upcoming')}
                  </span>
                ) : (
                  upcomingRewards.map(({ day, reward }) => (
                    <div className="calendar-upcoming-item" key={`up-${day}`}>
                      <span className="calendar-upcoming-day">{t('cal_day_label', { day })}</span>
                      <div className="calendar-upcoming-icon">
                        <img
                          src={resolveMediaUrl(reward.image)}
                          alt={reward.item_name}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = itemIconFallbackUrl();
                          }}
                        />
                        <span className="calendar-quantity-badge">x{reward.amount}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="calendar-aaa-hero-right" aria-hidden />
        </header>

        <div className="calendar-aaa-body">
          <div className="calendar-carousel-shell">
            <button
              type="button"
              className="calendar-carousel-arrow calendar-carousel-arrow--left"
              disabled={!canScrollLeft}
              aria-label={t('cal_prev')}
              onClick={() => scrollCarousel(-1)}
            >
              <i className="ph-light ph-caret-double-left" aria-hidden />
            </button>

            <div className="calendar-carousel-track-wrap">
              <div className="calendar-carousel" ref={carouselRef}>
                {carouselDays.map((day) => {
                  const reward = data?.calendarData[day] ?? data?.calendarData[String(day)];
                  if (!reward) return null;
                  const isToday = day === data?.todayDayNumber;
                  return (
                    <CalendarDayCard
                      key={day}
                      day={day}
                      reward={reward}
                      isToday={Boolean(isToday)}
                      canCheckinToday={Boolean(data?.canCheckinToday)}
                      isMilestone={day === milestoneDay}
                      eager={Boolean(isToday)}
                      onClaim={handleClaim}
                    />
                  );
                })}
                {loading && carouselDays.length === 0
                  ? Array.from({ length: 8 }, (_, i) => (
                      <div className="cal-day-card cal-day-card--skeleton" key={`sk-${i}`} aria-hidden />
                    ))
                  : null}
              </div>
            </div>

            <button
              type="button"
              className="calendar-carousel-arrow calendar-carousel-arrow--right"
              disabled={!canScrollRight}
              aria-label={t('cal_next')}
              onClick={() => scrollCarousel(1)}
            >
              <i className="ph-light ph-caret-double-right" aria-hidden />
            </button>
          </div>
        </div>

        <footer className="calendar-aaa-bonus">
          <div className="calendar-bonus-text">
            <div className="calendar-bonus-title">{t('cal_monthly_bonus')}</div>
            <div className="calendar-bonus-desc">
              {t('cal_monthly_bonus_desc', { days: bonusDay || 14 })}
            </div>
          </div>
          <div className="calendar-bonus-bar-wrap">
            <div className="calendar-bonus-bar">
              <div className="calendar-bonus-bar-fill" style={{ width: `${bonusProgress}%` }} />
            </div>
            <div className="calendar-bonus-count">
              {data ? `${data.daysChecked} / ${bonusDay || data.totalDays}` : '— / —'}{' '}
              {t('cal_bonus_days_unit')}
            </div>
          </div>
          <div className="calendar-bonus-preview">
            {bonusMilestones.length > 0 ? (
              bonusMilestones.map((reward) => (
                <div className="calendar-bonus-slot" key={`bonus-${reward.day}-${reward.CodeName128}`}>
                  <div className="calendar-bonus-preview-icon" title={reward.item_name}>
                    <img
                      src={resolveMediaUrl(reward.image)}
                      alt={reward.item_name}
                      title={reward.item_name}
                      loading="eager"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = itemIconFallbackUrl();
                      }}
                    />
                    {(reward.status ?? 0) < 2 ? (
                      <i className="ph-fill ph-lock-simple calendar-bonus-lock" aria-hidden />
                    ) : (
                      <i className="ph-fill ph-check-circle calendar-bonus-check" aria-hidden />
                    )}
                  </div>
                  <span className="calendar-bonus-preview-label">
                    {bonusMilestoneLabel(reward.day, t)}
                  </span>
                </div>
              ))
            ) : (
              <span className="calendar-upcoming-empty">—</span>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
